import { Ban, Download, FileText, Printer, RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import ComprobanteEgreso from './ComprobanteEgreso';
import { useComprobanteEgreso } from '../hooks/useComprobanteEgreso';
import { useImpresionCheque } from '../hooks/useImpresionCheque';
import ModalMotivo from './ModalMotivo';
import VistaPreviaImpresion from './VistaPreviaImpresion';

const FILTROS_INICIALES = { banco: '', estado: '', fecha_desde: '', fecha_hasta: '', numero_cheque: '', beneficiario: '' };

const ESTADOS = {
  pendiente: { label: 'Pendiente', clase: 'badge-pendiente' },
  aprobado: { label: 'Aprobado', clase: 'badge-aprobado' },
  rechazado: { label: 'Rechazado', clase: 'badge-rechazado' },
  impreso: { label: 'Impreso', clase: 'badge-impreso' },
  anulado: { label: 'Anulado', clase: 'badge-anulado' },
};

export default function Historial({ miRol }) {
  const esAprobador = miRol === 'admin' || miRol === 'aprobador';
  const [bancos, setBancos] = useState([]);
  const [cheques, setCheques] = useState([]);
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [cargando, setCargando] = useState(true);
  const [exportando, setExportando] = useState(false);
  const [modalMotivo, setModalMotivo] = useState(null); // { tipo: 'reimpresion' | 'anular', cheque }

  const { preparando, prepararImpresion, confirmarImpresion, cancelar } = useImpresionCheque(cargarCheques);
  const {
    preparando: preparandoComprobante,
    prepararComprobante,
    imprimir: imprimirComprobante,
    cancelar: cancelarComprobante,
  } = useComprobanteEgreso();

  useEffect(() => {
    api.get('/bancos/').then((res) => setBancos(res.data));
  }, []);

  function cargarCheques() {
    setCargando(true);
    const params = { page: pagina, ...limpiar(filtros) };
    api
      .get('/cheques/', { params })
      .then((res) => {
        setCheques(res.data.results);
        setTotalPaginas(Math.max(1, Math.ceil(res.data.count / 30)));
      })
      .finally(() => setCargando(false));
  }

  useEffect(() => {
    cargarCheques();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, filtros]);

  function limpiar(obj) {
    return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== ''));
  }

  function setFiltro(campo, valor) {
    setFiltros((f) => ({ ...f, [campo]: valor }));
    setPagina(1);
  }

  async function handleExportar() {
    setExportando(true);
    try {
      const res = await api.get('/cheques/exportar/', { params: limpiar(filtros), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = 'historial_cheques.csv';
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo exportar el historial.');
    } finally {
      setExportando(false);
    }
  }

  async function confirmarModalMotivo(motivo) {
    const { tipo, cheque } = modalMotivo;
    try {
      if (tipo === 'reimpresion') {
        await api.post(`/cheques/${cheque.id}/solicitar-reimpresion/`, { motivo });
        toast.success('Solicitud de reimpresión enviada. Queda pendiente de aprobación.');
      } else {
        await api.post(`/cheques/${cheque.id}/anular/`, { motivo });
        toast.success('Cheque anulado.');
      }
      setModalMotivo(null);
      cargarCheques();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo completar la acción.');
    }
  }

  return (
    <div className="historial">
      <div className="no-imprimir">
      <div className="control-group filtros-historial">
        <div className="fila-filtros">
          <div>
            <label>Beneficiario</label>
            <input value={filtros.beneficiario} onChange={(e) => setFiltro('beneficiario', e.target.value)} placeholder="Ej. Juan Pérez" />
          </div>
          <div>
            <label>Banco</label>
            <select value={filtros.banco} onChange={(e) => setFiltro('banco', e.target.value)}>
              <option value="">Todos</option>
              {bancos.map((b) => (
                <option key={b.id} value={b.id}>{b.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label>N.º de cheque</label>
            <input value={filtros.numero_cheque} onChange={(e) => setFiltro('numero_cheque', e.target.value)} />
          </div>
          <div>
            <label>Estado</label>
            <select value={filtros.estado} onChange={(e) => setFiltro('estado', e.target.value)}>
              <option value="">Todos</option>
              {Object.entries(ESTADOS).map(([valor, { label }]) => (
                <option key={valor} value={valor}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Desde</label>
            <input type="date" value={filtros.fecha_desde} onChange={(e) => setFiltro('fecha_desde', e.target.value)} />
          </div>
          <div>
            <label>Hasta</label>
            <input type="date" value={filtros.fecha_hasta} onChange={(e) => setFiltro('fecha_hasta', e.target.value)} />
          </div>
        </div>
        <button className="btn-secundario" onClick={handleExportar} disabled={exportando}>
          <Download size={16} /> {exportando ? 'Exportando…' : 'Exportar CSV'}
        </button>
      </div>

      {cargando ? (
        <p>Cargando…</p>
      ) : cheques.length === 0 ? (
        <p>No se encontraron cheques con estos filtros.</p>
      ) : (
        <table className="tabla-historial">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Banco</th>
              <th>Beneficiario</th>
              <th className="celda-monto">Monto</th>
              <th>N.º cheque</th>
              <th>Estado</th>
              <th>Registrado el</th>
              <th>Imprimir</th>
              <th>Anulación</th>
            </tr>
          </thead>
          <tbody>
            {cheques.map((c) => (
              <tr key={c.id}>
                <td>{c.fecha_emision}</td>
                <td>{c.banco_nombre}</td>
                <td>{c.beneficiario}</td>
                <td className="celda-monto">${Number(c.monto_numero).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                <td>{c.numero_cheque || '—'}</td>
                <td>
                  <span className={`badge-estado ${ESTADOS[c.estado]?.clase || ''}`}>
                    {ESTADOS[c.estado]?.label || c.estado}
                  </span>
                  {c.estado === 'rechazado' && c.motivo_rechazo && (
                    <span className="motivo-rechazo" title={c.motivo_rechazo}> ⓘ</span>
                  )}
                  {c.estado === 'anulado' && c.motivo_anulacion && (
                    <span className="motivo-rechazo" title={c.motivo_anulacion}> ⓘ</span>
                  )}
                </td>
                <td>{new Date(c.creado_en).toLocaleString('es-EC')}</td>
                <td>
                  <div className="acciones-tabla">
                    {(c.estado === 'aprobado' || c.estado === 'impreso') && (
                      <button type="button" className="btn-secundario btn-imprimir" onClick={() => prepararComprobante(c)}>
                        <FileText size={14} /> Comprobante
                      </button>
                    )}
                    {c.estado === 'aprobado' && (
                      <button type="button" className="btn-secundario btn-imprimir" onClick={() => prepararImpresion(c)}>
                        <Printer size={14} /> Cheque
                      </button>
                    )}
                    {c.estado === 'impreso' && c.ultima_reimpresion?.estado === 'aprobada' && !c.ultima_reimpresion?.impreso_en && (
                      <button type="button" className="btn-secundario btn-imprimir" onClick={() => prepararImpresion(c)}>
                        <Printer size={14} /> Cheque (reimpresión)
                      </button>
                    )}
                    {c.estado === 'impreso' && c.ultima_reimpresion?.estado === 'pendiente' && (
                      <span className="ayuda">Reimpresión pendiente de aprobación</span>
                    )}
                    {c.estado === 'impreso'
                      && (!c.ultima_reimpresion || c.ultima_reimpresion.estado === 'rechazada' || c.ultima_reimpresion.impreso_en) && (
                      <button type="button" className="btn-secundario" onClick={() => setModalMotivo({ tipo: 'reimpresion', cheque: c })}>
                        <RotateCcw size={14} /> Solicitar reimpresión
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  <div className="acciones-tabla">
                    {esAprobador && (c.estado === 'aprobado' || c.estado === 'impreso') && (
                      <button type="button" className="btn-secundario btn-peligro" onClick={() => setModalMotivo({ tipo: 'anular', cheque: c })}>
                        <Ban size={14} /> Anular
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {totalPaginas > 1 && (
        <div className="paginacion">
          <button disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>← Anterior</button>
          <span>Página {pagina} de {totalPaginas}</span>
          <button disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)}>Siguiente →</button>
        </div>
      )}
      </div>

      <VistaPreviaImpresion preparando={preparando} onCancelar={cancelar} onConfirmar={confirmarImpresion} />
      <ComprobanteEgreso preparando={preparandoComprobante} onCancelar={cancelarComprobante} onImprimir={imprimirComprobante} />
      {modalMotivo && (
        <ModalMotivo
          titulo={modalMotivo.tipo === 'reimpresion' ? 'Solicitar reimpresión' : 'Anular cheque'}
          ayuda={
            modalMotivo.tipo === 'reimpresion'
              ? '¿Por qué necesitas reimprimir este cheque? (ej. se dañó al imprimirse, se extravió)'
              : '¿Por qué se anula este cheque? Esta acción no se puede deshacer.'
          }
          textoConfirmar={modalMotivo.tipo === 'reimpresion' ? 'Solicitar' : 'Anular'}
          onCancelar={() => setModalMotivo(null)}
          onConfirmar={confirmarModalMotivo}
        />
      )}
    </div>
  );
}
